import { Button, Input, Modal, toast } from "@devhop/ui";
import { nanoid } from "nanoid";
import { useEffect, useMemo, useState } from "react";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  usePositionSalaryBandsQuery,
  useReplacePositionSalaryBands,
} from "../api/queries";

/** Radix Select ບໍ່ຮອງຮັບ value ວ່າງ — ສະແດງຊ່ວງອ້າງເກຣດທີ່ບໍ່ມີໃນຕາຕະລາງດ້ານບົນ */
const BAND_GRADE_ORPHAN_SELECT_VALUE = "__position_band_orphan_grade__";

type GradeDraftRow = {
  key: string;
  code: string;
  label: string;
  sortOrder: string;
};

type BandDraftRow = {
  key: string;
  salaryGradeCode: string;
  minFullYears: string;
  maxFullYears: string;
  baseSalary: string;
};

function gradesToDraft(
  grades: Array<{ code: string; label: string; sortOrder: number }>,
): GradeDraftRow[] {
  return grades.map((g) => ({
    key: nanoid(),
    code: g.code,
    label: g.label,
    sortOrder: String(g.sortOrder ?? 0),
  }));
}

function bandsToDraft(
  bands: Array<{
    salaryGradeCode: string;
    minFullYears: number;
    maxFullYears: number | null;
    baseSalary: number;
  }>,
): BandDraftRow[] {
  return bands.map((b) => ({
    key: nanoid(),
    salaryGradeCode: b.salaryGradeCode,
    minFullYears: String(b.minFullYears),
    maxFullYears: b.maxFullYears == null ? "" : String(b.maxFullYears),
    baseSalary: String(b.baseSalary),
  }));
}

export function PositionSalaryBandsModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positionId: string;
  positionTitle: string;
}) {
  const { open, onOpenChange, positionId, positionTitle } = props;
  const { data, isLoading, isError } = usePositionSalaryBandsQuery(positionId, {
    enabled: open && !!positionId,
  });
  const replace = useReplacePositionSalaryBands(positionId);

  const [gradesDraft, setGradesDraft] = useState<GradeDraftRow[]>([]);
  const [bandsDraft, setBandsDraft] = useState<BandDraftRow[]>([]);

  useEffect(() => {
    if (!open || data == null) return;
    const gLen = Array.isArray(data.grades) ? data.grades.length : 0;
    const grades =
      gLen > 0
        ? gradesToDraft(
            (
              data.grades as Array<{
                code: string;
                label: string;
                sortOrder: number;
              }>
            ).map((x) => ({
              code: x.code,
              label: x.label,
              sortOrder: x.sortOrder,
            })),
          )
        : [
            {
              key: nanoid(),
              code: "G1",
              label: "ລະດັບ 1",
              sortOrder: "0",
            },
          ];
    setGradesDraft(grades);
    setBandsDraft(
      Array.isArray(data.bands) && data.bands.length > 0
        ? bandsToDraft(
            (
              data.bands as Array<{
                salaryGradeCode: string;
                minFullYears: number;
                maxFullYears: number | null;
                baseSalary: number;
              }>
            ).map((b) => ({
              salaryGradeCode: b.salaryGradeCode,
              minFullYears: b.minFullYears,
              maxFullYears: b.maxFullYears,
              baseSalary: b.baseSalary,
            })),
          )
        : [],
    );
  }, [open, data]);

  const gradeCodesInUse = useMemo(
    () => new Set(gradesDraft.map((r) => r.code.trim())),
    [gradesDraft],
  );

  const gradeCodeSelectOptions = useMemo(
    () =>
      gradesDraft
        .filter((g) => g.code.trim() !== "")
        .map((g) => ({
          value: g.code.trim(),
          label: g.code.trim(),
        })),
    [gradesDraft],
  );

  const addGrade = () => {
    setGradesDraft((rows) => [
      ...rows,
      { key: nanoid(), code: "", label: "", sortOrder: String(rows.length) },
    ]);
  };

  const removeGrade = (key: string) => {
    const row = gradesDraft.find((r) => r.key === key);
    if (
      row &&
      row.code.trim() !== "" &&
      bandsDraft.some((b) => b.salaryGradeCode === row.code.trim())
    ) {
      toast.error("ລົບຊ່ວງເງິນເດືອນທີ່ໃຊ້ລະດັບນີ້ກ່ອນ");
      return;
    }
    setGradesDraft((rows) => rows.filter((r) => r.key !== key));
  };

  const addBand = () => {
    const fallbackCode =
      gradesDraft.find((g) => g.code.trim())?.code.trim() ?? "G1";
    setBandsDraft((d) => [
      ...d,
      {
        key: nanoid(),
        salaryGradeCode: fallbackCode,
        minFullYears: "0",
        maxFullYears: "",
        baseSalary: "",
      },
    ]);
  };

  const handleSave = async () => {
    const codesSeen = new Set<string>();
    const grades: Array<{ code: string; label: string; sortOrder: number }> =
      [];
    for (const r of gradesDraft) {
      const code = r.code.trim();
      const label = r.label.trim();
      const ord = Number.parseInt(r.sortOrder.trim(), 10);
      if (code === "" || label === "") {
        toast.error("ກະລຸນາກອກລະຫັດລະດັບແລະຊື່ໃຫ້ຄົບ");
        return;
      }
      if (codesSeen.has(code)) {
        toast.error("ບໍ່ໃຫ້ກົດລະຫັດລະດັບຊ້ຳ");
        return;
      }
      codesSeen.add(code);
      if (!Number.isFinite(ord)) {
        toast.error("ລຽງຕາມເລກ (sort) ບໍ່ຖືກຕ້ອງ");
        return;
      }
      grades.push({ code, label, sortOrder: ord });
    }

    const bands: Array<{
      salaryGradeCode: string;
      minFullYears: number;
      maxFullYears: number | null;
      baseSalary: number;
    }> = [];
    for (const r of bandsDraft) {
      const sg = r.salaryGradeCode.trim();
      const min = Number.parseInt(r.minFullYears.trim(), 10);
      const salary = Number.parseFloat(String(r.baseSalary).replace(/,/g, ""));
      const maxRaw = r.maxFullYears.trim();
      if (!codesSeen.has(sg)) {
        toast.error(`ບໍ່ມີລະດັບ "${sg}" ຕາມແຖວລະດັບດ້ານບົນ`);
        return;
      }
      if (!Number.isFinite(min) || min < 0) {
        toast.error("ກະລຸນາປ້ອນປີເລີ່ມຕົ້ນໃຫ້ຖືກຕ້ອງ");
        return;
      }
      let maxExclusive: number | null = null;
      if (maxRaw !== "") {
        const parsedMax = Number.parseInt(maxRaw, 10);
        if (!Number.isFinite(parsedMax) || parsedMax <= min) {
          toast.error("ປີສິ້ນສຸດ (ບໍ່ຮວມ) ຕ້ອງວ່າງ ຫຼື ມີຄ່າຫຼາຍກວ່າປີເລີ່ມຕົ້ນ");
          return;
        }
        maxExclusive = parsedMax;
      }
      if (!Number.isFinite(salary) || salary <= 0) {
        toast.error("ກະລຸນາປ້ອນເງິນເດືອນພື້ນຖານໃຫ້ຖືກຕ້ອງ");
        return;
      }
      bands.push({
        salaryGradeCode: sg,
        minFullYears: min,
        maxFullYears: maxExclusive,
        baseSalary: salary,
      });
    }

    if (bands.length > 0 && grades.length === 0) {
      toast.error("ຕ້ອງມີອຍ່າງໜ້ອຍຫນຶ່ງລະດັບເງິນເດືອນ");
      return;
    }

    try {
      await replace.mutateAsync({ grades, bands });
      toast.success("ບັນທຶກສາຍເງິນເດືອນສໍາເລັດ");
      onOpenChange(false);
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      const byCode: Record<string, string> = {
        POSITION_SALARY_BANDS_OVERLAP: "ຊ່ວງປີຊ້ຳກັນພາຍໃນເກຣດດຽວກັນ",
        POSITION_SALARY_BANDS_DUPLICATE_MIN: "ມີປີເລີ່ມຕົ້ນຊ້ຳກັນໃນລະດັດດຽວກັນ",
        POSITION_SALARY_BANDS_INVALID_RANGE: "ຊ່ວງປີບໍ່ຖືກຕ້ອງ (max ຕ້ອງຫຼາຍກວ່າ min)",
        POSITION_SALARY_GRADES_DUPLICATE_CODE: "ລະຫັດລະດັບບໍ່ໃຫ້ຊ້ຳເຊັ່ນກັນ",
        POSITION_SALARY_SCALE_GRADES_REQUIRED: "ຕ້ອງມີລະດັບເມື່ອມີຊ່ວງ",
        POSITION_SALARY_SCALE_BAND_UNKNOWN_GRADE_CODE: "ຊ່ວງອ້າງລະດັບບໍ່ໃນລາຍການ",
      };
      toast.error(
        byCode[code] ?? (e instanceof Error ? e.message : "ບັນທຶກລົ້ມເຫຼວ"),
      );
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`ເງິນເດືອນຕາມອາຍຸ × ລະດັບ — ${positionTitle}`}
      size="lg"
    >
      <div className="space-y-6">
        <p className="text-muted-foreground text-sm">
          ກໍາຫນົດຫຼາຍລະດັບເງິນເດືອນຕໍ່ຕໍາແໜ່ງໜຶ່ງ; ແຕ່ລະລະດັບມີຊ່ວງປີເຕັມໃນຕໍາແໜ່ງ
          ຄົນໃດພົວເຂົ້າກັບລະດັບນັ້ນຈະໃຊ້ຕາຕະລາງປີຂອງລະດັບດັ່ງກ່າວ
        </p>
        {isError ? (
          <p className="text-destructive text-sm">ໂຫຼດຂໍ້ມູນລົ້ມເຫຼວ</p>
        ) : null}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-muted-foreground text-sm">
              ລະດັບເງິນເດືອນຕໍ່ຕໍາແໜ່ງ
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addGrade}
            >
              ເພີ່ມລະດັບ
            </Button>
          </div>
          <div className="max-h-[220px] overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="p-2 font-medium">ລະຫັດ</th>
                  <th className="p-2 font-medium">ຊື່</th>
                  <th className="p-2 font-medium">Sort</th>
                  <th className="w-12 p-2" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td className="p-4 text-muted-foreground" colSpan={4}>
                      ກໍາລັງໂຫຼດ...
                    </td>
                  </tr>
                ) : gradesDraft.length === 0 ? (
                  <tr>
                    <td className="p-4 text-muted-foreground" colSpan={4}>
                      ຍັງບໍ່ມີລະດັບ — ກົດ &quot;ເພີ່ມລະດັບ&quot;
                    </td>
                  </tr>
                ) : (
                  gradesDraft.map((row) => (
                    <tr key={row.key} className="border-b">
                      <td className="p-2">
                        <Input
                          className="font-mono"
                          value={row.code}
                          onChange={(e) =>
                            setGradesDraft((d) =>
                              d.map((x) =>
                                x.key === row.key
                                  ? { ...x, code: e.target.value }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={row.label}
                          onChange={(e) =>
                            setGradesDraft((d) =>
                              d.map((x) =>
                                x.key === row.key
                                  ? { ...x, label: e.target.value }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          className="max-w-[80px]"
                          value={row.sortOrder}
                          onChange={(e) =>
                            setGradesDraft((d) =>
                              d.map((x) =>
                                x.key === row.key
                                  ? { ...x, sortOrder: e.target.value }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="p-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeGrade(row.key)}
                        >
                          ລົບ
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-muted-foreground text-sm">
              ຊ່ວງອາຍຸໃນຕໍາແໜ່ງ (ປີເຕັມ)
            </span>
            <Button type="button" variant="outline" size="sm" onClick={addBand}>
              ເພີ່ມຊ່ວງ
            </Button>
          </div>
          <div className="max-h-[320px] overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50 text-left">
                  <th className="p-2 font-medium">ລະດັບ</th>
                  <th className="p-2 font-medium">ປີເລີ່ມ (ຮວມ)</th>
                  <th className="p-2 font-medium">ປີສິ້ນ (ບໍ່ຮວມ)</th>
                  <th className="p-2 font-medium">ເງິນພື້ນຖານ</th>
                  <th className="w-12 p-2" />
                </tr>
              </thead>
              <tbody>
                {bandsDraft.length === 0 ? (
                  <tr>
                    <td className="p-4 text-muted-foreground" colSpan={5}>
                      ຍັງບໍ່ມີຊ່ວງ — ກົດ &quot;ເພີ່ມຊ່ວງ&quot;
                    </td>
                  </tr>
                ) : (
                  bandsDraft.map((row) => {
                    const trimmed = row.salaryGradeCode.trim();
                    const orphan = !gradeCodesInUse.has(trimmed);
                    const bandGradeOptions = orphan
                      ? [
                          {
                            value: BAND_GRADE_ORPHAN_SELECT_VALUE,
                            label: "— ຂາດເກຣດ —",
                          },
                          ...gradeCodeSelectOptions,
                        ]
                      : gradeCodeSelectOptions;
                    return (
                      <tr key={row.key} className="border-b">
                        <td className="p-2">
                          <SimpleSelect
                            value={
                              orphan
                                ? BAND_GRADE_ORPHAN_SELECT_VALUE
                                : trimmed
                            }
                            onValueChange={(v) =>
                              setBandsDraft((d) =>
                                d.map((x) =>
                                  x.key === row.key
                                    ? {
                                        ...x,
                                        salaryGradeCode:
                                          v === BAND_GRADE_ORPHAN_SELECT_VALUE
                                            ? x.salaryGradeCode
                                            : v,
                                      }
                                    : x,
                                ),
                              )
                            }
                            options={bandGradeOptions}
                            placeholder="ເລືອກລະດັບ"
                            className="h-10 w-[120px]"
                          />
                        </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min={0}
                          className="max-w-[100px]"
                          value={row.minFullYears}
                          onChange={(e) =>
                            setBandsDraft((d) =>
                              d.map((x) =>
                                x.key === row.key
                                  ? { ...x, minFullYears: e.target.value }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          placeholder="—"
                          className="max-w-[100px]"
                          value={row.maxFullYears}
                          onChange={(e) =>
                            setBandsDraft((d) =>
                              d.map((x) =>
                                x.key === row.key
                                  ? { ...x, maxFullYears: e.target.value }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="max-w-[140px] font-mono"
                          value={row.baseSalary}
                          onChange={(e) =>
                            setBandsDraft((d) =>
                              d.map((x) =>
                                x.key === row.key
                                  ? { ...x, baseSalary: e.target.value }
                                  : x,
                              ),
                            )
                          }
                        />
                      </td>
                      <td className="p-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setBandsDraft((d) =>
                              d.filter((x) => x.key !== row.key),
                            )
                          }
                        >
                          ລົບ
                        </Button>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={replace.isPending || isLoading}
          >
            {replace.isPending ? "ກໍາລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
